import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ResolveResponse } from '@/types'
import { useVendorAuth } from '@/auth/VendorAuthProvider'
import { useBrand } from '@/brand/BrandProvider'
import { AuthLayout } from '@/components/AuthLayout'
import { PasswordInput, Spinner } from '@/components/ui'
import { api, RequestError } from '@/lib/api'

type Step = 'email' | 'select' | 'password' | 'forgot'

export function Login() {
  const { login } = useVendorAuth()
  const brand = useBrand()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('email')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tenants, setTenants] = useState<ResolveResponse['tenants']>([])
  const [tenantHint, setTenantHint] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)

  // Al salir del login (login exitoso o navegación afuera), no dejar la marca de otra
  // tienda pegada en el resto de la app.
  useEffect(() => () => brand.setPreview(undefined), [brand])

  async function handleResolve(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const res = await api.post<ResolveResponse>('/auth/resolve', { email: email.trim().toLowerCase() })
      setTenants(res.tenants)
      if (res.tenants.length <= 1) {
        brand.setPreview(res.brand)
        setStep('password')
      } else {
        setStep('select')
      }
    } catch {
      // El paso 1 nunca falla de forma que valga la pena mostrar: si el edge no responde,
      // se sigue igual a la contraseña con marca genérica.
      setStep('password')
    } finally {
      setBusy(false)
    }
  }

  function selectTenant(slug: string) {
    setTenantHint(slug)
    setStep('password')
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const { firstLogin, isAdmin, account } = await login(email.trim().toLowerCase(), password, tenantHint)
      if (!account) brand.refresh() // ahora el token identifica la tienda: re-leer /api/brand
      brand.setPreview(undefined)
      navigate(account ? '/onboarding' : firstLogin ? '/primer-ingreso' : isAdmin ? '/admin' : '/', { replace: true })
    } catch (e) {
      const err = e as RequestError
      setError(err.status === 401 ? 'Email o contraseña incorrectos.' : err.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.post('/auth/reset-request', { email: forgotEmail.trim().toLowerCase() })
    } catch {
      /* respuesta genérica igual */
    } finally {
      setForgotSent(true)
      setBusy(false)
    }
  }

  if (step === 'forgot') {
    return (
      <AuthLayout title="Recuperar contraseña" subtitle="Te enviamos un link a tu email">
        {forgotSent ? (
          <div className="card p-5 text-center">
            <p className="text-sm text-ink-soft">
              Si el email está registrado, vas a recibir un link para restablecer tu contraseña.
            </p>
            <button className="btn-primary btn-block mt-4" onClick={() => setStep('email')}>
              Volver al login
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgot} className="space-y-4">
            <p className="text-xs text-ink-muted">
              Esto es para cuentas de <strong>vendedor</strong>. Si entrás como administrador, tu
              contraseña la maneja Koru Suite, no nosotros —{' '}
              <a
                href="https://www.korusuite.com"
                target="_blank"
                rel="noreferrer"
                className="font-medium accent-text"
              >
                recuperala desde ahí
              </a>
              .
            </p>
            <div>
              <label className="field-label">Email</label>
              <input
                type="email"
                required
                className="field-input"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="vos@empresa.com"
              />
            </div>
            <button className="btn-primary btn-block" disabled={busy}>
              {busy ? <Spinner className="h-4 w-4" /> : 'Enviar link'}
            </button>
            <button
              type="button"
              className="btn-ghost btn-block"
              onClick={() => setStep('email')}
            >
              Volver
            </button>
          </form>
        )}
      </AuthLayout>
    )
  }

  if (step === 'select') {
    return (
      <AuthLayout title="¿Cuál es tu tienda?">
        <div className="space-y-2">
          {tenants.map((t) => (
            <button
              key={t.slug}
              type="button"
              className="card block w-full p-3 text-left text-sm"
              onClick={() => selectTenant(t.slug)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button type="button" className="btn-ghost btn-block mt-4" onClick={() => setStep('email')}>
          Volver
        </button>
      </AuthLayout>
    )
  }

  if (step === 'password') {
    return (
      <AuthLayout title="Ingresá tu contraseña">
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="rounded-lg bg-ink/5 px-3 py-2 text-sm text-ink-muted">{email}</div>
          <div>
            <label className="field-label">Contraseña</label>
            <PasswordInput
              required
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {error && <div className="rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{error}</div>}

          <button className="btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner className="h-4 w-4" /> : 'Ingresar'}
          </button>
          <button
            type="button"
            className="mx-auto block text-sm font-medium accent-text"
            onClick={() => setStep('forgot')}
          >
            Olvidé mi contraseña
          </button>
          <button
            type="button"
            className="btn-ghost btn-block"
            onClick={() => {
              setPassword('')
              setError(null)
              brand.setPreview(undefined)
              setStep('email')
            }}
          >
            Volver
          </button>
        </form>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Ingresá a tu cuenta">
      <form onSubmit={handleResolve} className="space-y-4">
        <div>
          <label className="field-label">Email</label>
          <input
            type="email"
            required
            autoFocus
            autoComplete="email"
            className="field-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vos@empresa.com"
          />
        </div>

        {error && <div className="rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{error}</div>}

        <button className="btn-primary btn-block" disabled={busy}>
          {busy ? <Spinner className="h-4 w-4" /> : 'Continuar'}
        </button>

        <button
          type="button"
          className="btn-secondary btn-block opacity-60"
          disabled
          title="Disponible próximamente"
        >
          Iniciar sesión con VTEX ID
        </button>

        <a
          href="https://www.korusuite.com/en/apps/power-seller"
          target="_blank"
          rel="noreferrer"
          className="mx-auto block text-center text-sm font-medium accent-text"
        >
          ¿No tenés cuenta? Contratá Power Seller en Koru Suite
        </a>
      </form>
    </AuthLayout>
  )
}
